// src/pages/CostEditor.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import VQModal from '../components/VQModal';
import {
  fetchMaterials,
  fetchBreakdownData,
  fetchQuantityCalculatorData,
  updateMaterialData,
  API_BASE_URL,
  fetchRegions,
  fetchMurCategories,
  fetchCostMaterials,           // ✅ ADD THIS
  fetchCostMaterialBreakdown,   // ✅ ADD THIS
  fetchCostMaterialLineItems,   // ✅ ADD THIS
  saveCostMaterialBreakdown,
  deleteCostMaterial,
} from '../services/api';
import api from '../services/api';
import './MaterialEditorPage.css'; // Reuse same styles
import QuantityCalculatorModal from './QuantityCalculatorModal';
import { logAction } from '../services/api';
import { auth } from '../firebaseConfig';
import { v4 as uuidv4 } from 'uuid';
import { resolveLineItemLinks } from '../utils/quantityLinks';
import axios from 'axios';
import { useRegion } from '../context/RegionContext';
import StyledDropdown from '../components/StyledDropdown';

const SuccessModal = ({ isOpen, onClose, title, message, icon }) => {
  if (!isOpen) return null;

  return (
    <div className="success-modal-overlay" onClick={onClose}>
      <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="success-modal-header">
          <div className="success-modal-icon">{icon || '✅'}</div>
          <h2>{title || 'Success!'}</h2>
        </div>
        <div className="success-modal-body">
          <p>{message}</p>
        </div>
        <div className="success-modal-footer">
          <button className="success-modal-button" onClick={onClose}>OK</button>
        </div>
      </div>
    </div>
  );
};

const ErrorModal = ({ isOpen, onClose, title, message, icon }) => {
  if (!isOpen) return null;

  return (
    <div className="success-modal-overlay" onClick={onClose}>
      <div className="success-modal-content error-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="success-modal-header error-modal-header">
          <div className="success-modal-icon error-modal-icon">{icon || '⚠️'}</div>
          <h2>{title || 'Error'}</h2>
        </div>
        <div className="success-modal-body">
          <p style={{ whiteSpace: 'pre-line' }}>{message}</p>
        </div>
        <div className="success-modal-footer">
          <button className="success-modal-button error-modal-button" onClick={onClose}>OK</button>
        </div>
      </div>
    </div>
  );
};

/* ========= Constants ========= */
const TYPE_BASE = { RATE: 'Rate' };

const dsidToSuffix = (dsid) => {
  const map = {
    vquest: '',
    'vquest-au-1': '-AU',
    'vquest-us-1': '-US',
    'vquest-uk-ICE': '-UK',
    'vquest-irl-1': '-IRL',
    'vquest-midrise': '-Midrise',
  };
  return map[dsid] ?? '';
};

const normalizeRegionToDSID = (r) => {
  const map = {
    nz: 'vquest',
    au: 'vquest-au-1',
    us: 'vquest-us-1',
    irl: 'vquest-irl-1',
    uk: 'vquest-uk-ICE',
    mr: 'vquest-midrise',
    vquest: 'vquest',
    'vquest-au-1': 'vquest-au-1',
    'vquest-us-1': 'vquest-us-1',
    'vquest-irl-1': 'vquest-irl-1',
    'vquest-uk-ICE': 'vquest-uk-ICE',
    'vquest-midrise': 'vquest-midrise',
  };
  return map[r] || r || 'vquest';
};

function CostEditor() {
  const location = useLocation();
  const navigate = useNavigate();
  const preselectedMaterial = location.state?.materialId || '';

  const [errorModal, setErrorModal] = useState({
    open: false,
    title: '',
    message: '',
    icon: '⚠️',
  });

  const showErrorModal = (title, message, icon = '⚠️') => {
    setErrorModal({ open: true, title, message, icon });
  };

  const [modal, setModal] = useState({
    open: false,
    title: '',
    message: '',
    icon: '✅',
  });
  const showModal = (title, message, icon = '✅') =>
    setModal({ open: true, title, message, icon });

  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (msg) => {
      try {
        showModal('Notice', String(msg ?? ''), 'ℹ️');
      } catch {
        originalAlert(msg);
      }
    };
    return () => {
      window.alert = originalAlert;
    };
  }, []);

  const { selectedRegion } = useRegion();

  const [availableRegions, setAvailableRegions] = useState([]);
  const regionName = useMemo(() => {
    const region = availableRegions.find((r) => r.id === selectedRegion);
    return region ? region.name : selectedRegion;
  }, [availableRegions, selectedRegion]);

  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState(preselectedMaterial);

  const [costLineItems, setCostLineItems] = useState([]);
  const [editingLineItems, setEditingLineItems] = useState({});
  const [costCategories, setCostCategories] = useState([]);

  // Add these state variables with your other state declarations
  const [showEditSKUModal, setShowEditSKUModal] = useState(false);
  const [newSKU, setNewSKU] = useState('');
  const [originalSKU, setOriginalSKU] = useState('');

  const [showEditVendorModal, setShowEditVendorModal] = useState(false);
  const [newVendor, setNewVendor] = useState('');
  const [originalVendor, setOriginalVendor] = useState('');


  const [modalOpen, setModalOpen] = useState(false);
  const [activeLineItem, setActiveLineItem] = useState(null);
  const [rowHistory, setRowHistory] = useState({});
  const [originalData, setOriginalData] = useState([]);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit Name functionality
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [newMaterialName, setNewMaterialName] = useState('');
  const [originalMaterialName, setOriginalMaterialName] = useState('');

  const measurementIdFor = useCallback(
    (base) => {
      const dsid = normalizeRegionToDSID(selectedRegion);
      const suffix = dsidToSuffix(dsid);
      return `vquest-${base}${suffix}`;
    },
    [selectedRegion]
  );

  // Load region names
  useEffect(() => {
    (async () => {
      try {
        const regions = await fetchRegions();
        setAvailableRegions(regions);
      } catch (e) {
        console.error('Error loading regions:', e);
      }
    })();
  }, []);

  useEffect(() => {
    if (selectedRegion) localStorage.setItem('selectedRegion', selectedRegion);
  }, [selectedRegion]);

// ✅ Replace loadMaterials with this
const loadCostMaterials = useCallback(async () => {
  if (!selectedRegion) return;
  try {
    setLoading(true);
    console.log('🔍 COST EDITOR: Loading cost materials for region:', selectedRegion);
    
    const response = await fetchCostMaterials(selectedRegion);
    console.log('📌 Cost materials response:', response);
    
    const list = response.data || [];
    setMaterials(list);
    setFilteredMaterials(list);
  } catch (err) {
    console.error('Error loading cost materials:', err);
    showErrorModal('Failed to load cost materials', err?.message || String(err));
  } finally {
    setLoading(false);
  }
}, [selectedRegion]);


  const loadCategories = useCallback(async () => {
    try {
      const rate = await fetchMurCategories(measurementIdFor(TYPE_BASE.RATE));
      setCostCategories(rate.data || []);
    } catch (err) {
      console.error('Error loading categories:', err);
      showErrorModal('Failed to load categories', err?.message || String(err));
    }
  }, [measurementIdFor]);

  const categoryForDesc = (desc, pool) => {
    const hit = (pool || []).find((c) => c.MeasurementUnitName === desc);
    return hit?.Category || '';
  };

// ✅ FIX: Add breakdownData assignment before enrichment
// ✅ FIX: Add breakdownData assignment before enrichment
const loadBreakdownData = useCallback(
  async (measurementUnitRateId) => {
    if (!selectedRegion || !measurementUnitRateId) return;
    try {
      setLoading(true);

      // Fetch breakdown data
      const breakdownResponse = await fetchCostMaterialBreakdown(measurementUnitRateId, selectedRegion);
      let breakdownData = breakdownResponse.data?.breakdownItems || [];
      console.log('✅ Breakdown data:', breakdownData.length, 'items');

      // ✅ NEW: Fallback logic - If no breakdown exists, create default line item
      if (breakdownData.length === 0) {
        // Find the selected cost material from materials array
        const selectedCostMaterial = materials.find(m => String(m.MeasurementUnitRateId) === String(measurementUnitRateId));
        
        if (selectedCostMaterial) {
          console.log('⚠️ No breakdown found - creating default line item from cost material itself');
          
          const midRATE = measurementIdFor(TYPE_BASE.RATE);
          
          // Create a synthetic breakdown item using the cost material's own data
          breakdownData = [{
            FactorId: uuidv4(),
            MeasurementType: midRATE,
            MeasurementUnitRateId: selectedCostMaterial.MeasurementUnitRateId,
            CostMeasurementName: selectedCostMaterial.MeasurementUnitName,
            QuantityTypeName: selectedCostMaterial.QuantityTypeName || 'Each',
            Value: parseFloat(selectedCostMaterial.Value) || 0,
            Factor: 1, // Default factor of 1
            Category: categoryForDesc(selectedCostMaterial.MeasurementUnitName, costCategories),
            TotalResult: parseFloat(selectedCostMaterial.Value) || 0,
          }];
          
          console.log('✅ Created default line item:', breakdownData[0]);
        }
      }

      setOriginalData(breakdownData.map((item) => ({ ...item })));

      // Fetch line items (quantity calculator data)
      const lineItemsResponse = await fetchCostMaterialLineItems(measurementUnitRateId, selectedRegion);
      const lineItemsData = lineItemsResponse.data?.lineItems || [];
      console.log('✅ Line items data:', lineItemsData.length, 'items');

      // Build rowHistory
      const updatedRowHistory = {};
      lineItemsData.forEach((lineItem) => {
        if (lineItem.id && lineItem.line_item_rows) {
          try {
            updatedRowHistory[lineItem.id] = JSON.parse(lineItem.line_item_rows);
          } catch (err) {
            console.error('Error parsing line_item_rows:', err);
          }
        }
      });

      const midRATE = measurementIdFor(TYPE_BASE.RATE);

      const enrich = (items, pool, expectedMid) =>
        (items || [])
          .filter((i) => String(i.MeasurementType || '') === expectedMid)
          .map((item) => {
            const li = lineItemsData.find((x) => x.id === item.FactorId);
            const desc = item.CostMeasurementName || '';
            const heading = li?.headings || '';
            const sortOrder = li?.sort_order !== undefined ? li.sort_order : 9999;

            let finalFactor = parseFloat(item.Factor) || 0;

            if (li && li.factor !== undefined && li.factor !== null) {
              const hasCalculatorRows =
                li.line_item_rows &&
                li.line_item_rows !== '[]' &&
                JSON.parse(li.line_item_rows || '[]').length > 0;

              if (hasCalculatorRows) {
                const calcFactor = parseFloat(li.factor) || 0;
                finalFactor = calcFactor;
              }
            }

            return {
              ...item,
              MeasurementId: item.MeasurementType || expectedMid,
              MeasurementType: item.MeasurementType || expectedMid,
              Code: item.MeasurementUnitRateId || '',
              Factor: finalFactor,
              LineItemRows: li ? JSON.parse(li.line_item_rows || '[]') : [],
              heading: heading,
              sortOrder: sortOrder,
              isHeading: false,
              Category: categoryForDesc(desc, pool),
              TotalResult: finalFactor * (parseFloat(item.Value) || 0),
            };
          });

      // ✅ FIX: Use breakdownData instead of undefined 'data'
      const enrichedCost = enrich(breakdownData, costCategories, midRATE);

      const sortBySortOrder = (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0);
      enrichedCost.sort(sortBySortOrder);

      console.log('✅ Sorted enrichedCost by sort_order:', enrichedCost.map(i => ({
        id: i.FactorId,
        heading: i.heading,
        sortOrder: i.sortOrder
      })));

      const insertHeadingRowsIfNeeded = (enrichedItems) => {
        const hasHeadingRows = enrichedItems.some((item) => item.isHeading === true);

        if (hasHeadingRows) {
          console.log('✅ Heading rows already exist, skipping insertion');
          return enrichedItems;
        }

        console.log('📋 Creating heading rows from metadata');
        const result = [];
        let currentHeading = null;

        enrichedItems.forEach((item) => {
          if (item.heading && item.heading.trim() !== '' && item.heading !== currentHeading) {
            currentHeading = item.heading;
            result.push({
              FactorId: `heading-${uuidv4()}`,
              isHeading: true,
              heading: currentHeading,
              MeasurementId: '',
              MeasurementType: '',
              CostMeasurementName: '',
              Code: '',
              QuantityTypeName: '',
              Factor: 0,
              Value: 0,
              TotalResult: 0,
              Category: '',
            });
          }
          result.push(item);
        });

        return result;
      };

      // ✅ Live links: re-read linked calculator rows against current sibling quantities
      // and recompute dependent factors before rendering.
      resolveLineItemLinks(enrichedCost, {
        rowsOf: (item) => updatedRowHistory[item.FactorId],
        setRows: (item, rows) => { updatedRowHistory[item.FactorId] = rows; },
        setQuantity: (item, qty) => {
          item.Factor = qty;
          item.TotalResult = qty * (parseFloat(item.Value) || 0);
        },
      });

      setCostLineItems(insertHeadingRowsIfNeeded(enrichedCost));
      setRowHistory(updatedRowHistory);

      // ✅ FIX: Use MeasurementUnitRateId instead of Id
      const currentMaterial = materials.find((m) => String(m.MeasurementUnitRateId) === String(measurementUnitRateId));
      if (currentMaterial) {
        setOriginalMaterialName(currentMaterial.MeasurementUnitName);
        setNewMaterialName(currentMaterial.MeasurementUnitName);

        // 🆕 Add these lines for SKU and Vendor
        setOriginalSKU(currentMaterial.SKU || '');
        setNewSKU(currentMaterial.SKU || '');
        setOriginalVendor(currentMaterial.Vendor || '');
        setNewVendor(currentMaterial.Vendor || '');
      }

      setSuccess(`Cost material data loaded successfully for ${regionName}`);
    } catch (error) {
      console.error('Error fetching breakdown data:', error);
      showErrorModal('Failed to load breakdown data', error?.message || String(error));
    } finally {
      setLoading(false);
    }
  },
  [selectedRegion, costCategories, measurementIdFor, regionName, materials]
);



  const getComparisonData = () => {
    return originalData.map((originalItem) => {
      const currentItem = costLineItems.find(
        (item) =>
          item.FactorId === originalItem.FactorId &&
          item.MeasurementType === originalItem.MeasurementType
      );

      return {
        original: originalItem,
        current: currentItem || {
          MeasurementId: 'DELETED',
          MeasurementType: 'DELETED',
          CostMeasurementName: 'DELETED',
          Code: 'DELETED',
          QuantityTypeName: 'DELETED',
          Value: 'DELETED',
          Factor: 'DELETED',
          TotalResult: 'DELETED',
        },
      };
    });
  };

  useEffect(() => {
    loadCostMaterials();
    loadCategories();
    setSelectedMaterial('');
    setCostLineItems([]);
    setRowHistory({});
    setOriginalData([]);
    setOriginalMaterialName('');
    setNewMaterialName('');
  }, [loadCostMaterials, loadCategories, selectedRegion]);

  useEffect(() => {
    if (preselectedMaterial && selectedRegion) {
      setSelectedMaterial(preselectedMaterial);
      loadBreakdownData(preselectedMaterial);
    }
  }, [preselectedMaterial, selectedRegion, loadBreakdownData]);

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    
    // ✅ Search cost items by ID or Name
    const filtered = materials.filter((costItem) =>
      costItem.MeasurementUnitRateId?.toString().toLowerCase().includes(query) ||
      costItem.MeasurementUnitName?.toLowerCase().includes(query)
    );
    setFilteredMaterials(filtered);
  };


  const handleMaterialChange = async (e) => {
    const costItemId = e.target.value;  // ✅ This is now a MeasurementUnitRateId
    setSelectedMaterial(costItemId);
    setSuccess('');
    
    if (costItemId) {
      // ✅ Find the cost item to get its associated Quantity Unit ID
      const selectedCostItem = materials.find(m => String(m.MeasurementUnitRateId) === String(costItemId));
      
      if (selectedCostItem) {
        console.log('✅ Selected Cost Item:', selectedCostItem);
        
        // ✅ You'll need to fetch the Quantity Unit ID associated with this cost item
        // For now, we'll use the cost item's MeasurementUnitRateId
        await loadBreakdownData(costItemId);
      } else {
        console.error('❌ Cost item not found:', costItemId);
      }
    } else {
      setCostLineItems([]);
      setOriginalMaterialName('');
      setNewMaterialName('');
    }
  };


  const handleEditNameClick = () => {
    if (!selectedMaterial) {
      showErrorModal('Error', 'Please select a material first');
      return;
    }
    setShowEditNameModal(true);
  };

  const handleSaveName = async () => {
  if (!newMaterialName.trim()) {
    showErrorModal('Error', 'Material name cannot be empty');
    return;
  }

  if (newMaterialName.trim() === originalMaterialName) {
    setShowEditNameModal(false);
    return;
  }

  try {
    setLoading(true);
    
    const token = await auth.currentUser?.getIdToken();
    
    // ✅ FIXED: Use the correct Cost Editor endpoint
    const response = await axios.put(
      `${API_BASE_URL}/cost-editor/cost-materials/${selectedMaterial}/name`,
      { name: newMaterialName.trim() },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-region': selectedRegion,
          'x-user-id': auth.currentUser?.uid || 'unknown',
          'x-user-email': auth.currentUser?.email || 'unknown',
        },
      }
    );

    if (response.data.success) {
      // Update local state - use MeasurementUnitRateId not Id
      setMaterials((prev) =>
        prev.map((m) =>
          String(m.MeasurementUnitRateId) === String(selectedMaterial)
            ? { ...m, MeasurementUnitName: newMaterialName.trim() }
            : m
        )
      );
      setFilteredMaterials((prev) =>
        prev.map((m) =>
          String(m.MeasurementUnitRateId) === String(selectedMaterial)
            ? { ...m, MeasurementUnitName: newMaterialName.trim() }
            : m
        )
      );

      setOriginalMaterialName(newMaterialName.trim());
      setShowEditNameModal(false);

      showModal(
        'Name Updated',
        `Cost material name successfully updated to "${newMaterialName.trim()}" for ${regionName}.`,
        '✏️'
      );
      setSuccess(`Cost material name updated successfully for ${regionName}`);

      logAction(
        auth.currentUser?.uid || 'unknown',
        auth.currentUser?.email || 'unknown',
        'UPDATE_COST_MATERIAL_NAME',
        {
          materialId: selectedMaterial,
          oldName: originalMaterialName,
          newName: newMaterialName.trim(),
          regionId: selectedRegion,
          regionName,
        }
      );
    } else {
      showErrorModal('Error', 'Failed to update cost material name.');
    }
  } catch (error) {
    console.error('Error updating cost material name:', error);
    showErrorModal(
      'Error',
      'An error occurred while updating the cost material name: ' +
        (error.response?.data?.message || error.message)
    );
  } finally {
    setLoading(false);
  }
};


  // Handle Edit SKU functionality
const handleEditSKUClick = () => {
  if (!selectedMaterial) {
    showErrorModal('Error', 'Please select a material first');
    return;
  }
  setShowEditSKUModal(true);
};

const handleSaveSKU = async () => {
  if (newSKU.trim() === originalSKU) {
    setShowEditSKUModal(false);
    return;
  }

  try {
    setLoading(true);
    
    const token = await auth.currentUser?.getIdToken();
    
    const response = await axios.put(
      `${API_BASE_URL}/cost-editor/cost-materials/${selectedMaterial}/sku`,
      { sku: newSKU.trim() },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-region': selectedRegion,
          'x-user-id': auth.currentUser?.uid || 'unknown',
          'x-user-email': auth.currentUser?.email || 'unknown',
        },
      }
    );

    if (response.data.success) {
      // Update local state
      setMaterials((prev) =>
        prev.map((m) =>
          String(m.MeasurementUnitRateId) === String(selectedMaterial)
            ? { ...m, SKU: newSKU.trim() }
            : m
        )
      );
      setFilteredMaterials((prev) =>
        prev.map((m) =>
          String(m.MeasurementUnitRateId) === String(selectedMaterial)
            ? { ...m, SKU: newSKU.trim() }
            : m
        )
      );

      setOriginalSKU(newSKU.trim());
      setShowEditSKUModal(false);

      showModal(
        'SKU Updated',
        `SKU successfully updated to "${newSKU.trim()}" for ${regionName}.`,
        '🏷️'
      );
      setSuccess(`SKU updated successfully for ${regionName}`);

      logAction(
        auth.currentUser?.uid || 'unknown',
        auth.currentUser?.email || 'unknown',
        'UPDATE_COST_SKU',
        {
          materialId: selectedMaterial,
          oldSKU: originalSKU,
          newSKU: newSKU.trim(),
          regionId: selectedRegion,
          regionName,
        }
      );
    } else {
      showErrorModal('Error', 'Failed to update SKU.');
    }
  } catch (error) {
    console.error('Error updating SKU:', error);
    showErrorModal(
      'Error',
      'An error occurred while updating the SKU: ' +
        (error.response?.data?.message || error.message)
    );
  } finally {
    setLoading(false);
  }
};

// Handle Edit Vendor functionality
const handleEditVendorClick = () => {
  if (!selectedMaterial) {
    showErrorModal('Error', 'Please select a material first');
    return;
  }
  setShowEditVendorModal(true);
};

const handleSaveVendor = async () => {
  if (newVendor.trim() === originalVendor) {
    setShowEditVendorModal(false);
    return;
  }

  try {
    setLoading(true);
    
    const token = await auth.currentUser?.getIdToken();
    
    const response = await axios.put(
      `${API_BASE_URL}/cost-editor/cost-materials/${selectedMaterial}/vendor`,
      { vendor: newVendor.trim() },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-region': selectedRegion,
          'x-user-id': auth.currentUser?.uid || 'unknown',
          'x-user-email': auth.currentUser?.email || 'unknown',
        },
      }
    );

    if (response.data.success) {
      // Update local state
      setMaterials((prev) =>
        prev.map((m) =>
          String(m.MeasurementUnitRateId) === String(selectedMaterial)
            ? { ...m, Vendor: newVendor.trim() }
            : m
        )
      );
      setFilteredMaterials((prev) =>
        prev.map((m) =>
          String(m.MeasurementUnitRateId) === String(selectedMaterial)
            ? { ...m, Vendor: newVendor.trim() }
            : m
        )
      );

      setOriginalVendor(newVendor.trim());
      setShowEditVendorModal(false);

      showModal(
        'Vendor Updated',
        `Vendor successfully updated to "${newVendor.trim()}" for ${regionName}.`,
        '🏢'
      );
      setSuccess(`Vendor updated successfully for ${regionName}`);

      logAction(
        auth.currentUser?.uid || 'unknown',
        auth.currentUser?.email || 'unknown',
        'UPDATE_COST_VENDOR',
        {
          materialId: selectedMaterial,
          oldVendor: originalVendor,
          newVendor: newVendor.trim(),
          regionId: selectedRegion,
          regionName,
        }
      );
    } else {
      showErrorModal('Error', 'Failed to update Vendor.');
    }
  } catch (error) {
    console.error('Error updating Vendor:', error);
    showErrorModal(
      'Error',
      'An error occurred while updating the Vendor: ' +
        (error.response?.data?.message || error.message)
    );
  } finally {
    setLoading(false);
  }
};

  const uniqueCategoryOptions = (cats) => {
    const seen = new Set();
    const out = [];
    for (const c of cats || []) {
      const cat = (c.Category || '').trim();
      if (!cat || seen.has(cat)) continue;
      seen.add(cat);
      out.push({ value: cat, label: cat });
    }
    return out;
  };

  const uniqueDescOptions = (cats, category) => {
    const seen = new Set();
    const out = [];
    for (const c of cats || []) {
      if (category && c.Category !== category) continue;
      const n = c?.MeasurementUnitName;
      if (!n || seen.has(n)) continue;
      seen.add(n);
      out.push({ value: n, label: n });
    }
    return out;
  };

  const handleLineItemCategoryChange = (index, category) => {
    const updated = [...costLineItems];
    const item = updated[index] || {};
    let keep = false;

    if (item.CostMeasurementName) {
      const rec = costCategories.find(
        (c) => c.Category === category && c.MeasurementUnitName === item.CostMeasurementName
      );
      keep = !!rec;
    }

    updated[index] = {
      ...item,
      Category: category || '',
      ...(keep
        ? {}
        : {
            CostMeasurementName: '',
            Code: '',
            Value: 0,
            QuantityTypeName: '',
            TotalResult: 0,
          }),
    };

    setCostLineItems(updated);
  };

  const handleLineItemChange = (index, field, value) => {
    const updated = [...costLineItems];

    if (field === 'description') {
      const sel = costCategories.find((cat) => cat.MeasurementUnitName === value);
      if (sel) {
        const regionMID = measurementIdFor(TYPE_BASE.RATE);

        updated[index] = {
          ...updated[index],
          CostMeasurementName: sel.MeasurementUnitName || '',
          MeasurementId: regionMID,
          MeasurementType: regionMID,
          Code: sel.MeasurementUnitRateId || '',
          QuantityTypeName: sel.QuantityTypeName || '',
          Value: sel.Value || 0,
          Factor: updated[index].Factor || 0,
          TotalResult: (sel.Value || 0) * (updated[index].Factor || 0),
          FactorId: updated[index].FactorId || uuidv4(),
          Category: sel.Category || '',
        };
      } else {
        updated[index].CostMeasurementName = value;
      }
    } else if (field === 'Factor' || field === 'Value') {
      updated[index][field] = parseFloat(value) || 0;
      updated[index].TotalResult =
        (parseFloat(updated[index].Factor) || 0) * (parseFloat(updated[index].Value) || 0);
    }

    setCostLineItems(updated);

    logAction(
      auth.currentUser?.uid || 'unknown',
      auth.currentUser?.email || 'unknown',
      'UPDATE_LINE_ITEM',
      {
        ...updated[index],
        regionId: selectedRegion,
        regionName,
      }
    );
  };

  const addHeading = () => {
    const newHeading = {
      FactorId: uuidv4(),
      isHeading: true,
      heading: 'New Heading',
      MeasurementId: '',
      MeasurementType: '',
      CostMeasurementName: '',
      Code: '',
      QuantityTypeName: '',
      Factor: 0,
      Value: 0,
      TotalResult: 0,
      Category: '',
    };

    setCostLineItems((prev) => [...prev, newHeading]);
  };

  const handleHeadingChange = (index, value) => {
    const updatedItems = [...costLineItems];
    if (updatedItems[index]) {
      updatedItems[index].heading = value;
      setCostLineItems(updatedItems);
    }
  };

  const toggleEdit = (index) => {
    setEditingLineItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const addLineItem = () => {
    const measurementId = measurementIdFor(TYPE_BASE.RATE);
    const newItem = {
      FactorId: uuidv4(),
      MeasurementType: measurementId,
      MeasurementId: measurementId,
      CostMeasurementName: '',
      Code: '',
      QuantityTypeName: '',
      Factor: 0,
      Value: 0,
      TotalResult: 0,
      BreakdownData: [],
      isHeading: false,
      Category: '',
    };
    setCostLineItems((prev) => [...prev, newItem]);
  };

  const deleteLineItem = (index) => {
    const updated = [...costLineItems];
    const deletedItem = updated.splice(index, 1)[0];
    setCostLineItems(updated);

    setRowHistory((prevHistory) => {
      const newHistory = { ...prevHistory };
      if (deletedItem?.FactorId) delete newHistory[deletedItem.FactorId];
      return newHistory;
    });
  };

  const saveQuantityFromModal = async (calculatedQuantity, updatedRows) => {
    console.log('💾 saveQuantityFromModal called with:', {
      calculatedQuantity,
      updatedRowsCount: updatedRows.length,
      activeLineItem: activeLineItem,
    });

    const { index } = activeLineItem;
    const updated = [...costLineItems];

    const finalQuantity = parseFloat(calculatedQuantity) || 0;
    updated[index].Factor = finalQuantity;
    updated[index].TotalResult = parseFloat(updated[index].Value || 0) * finalQuantity;

    const currentFactorId = updated[index].FactorId || uuidv4();
    updated[index].FactorId = currentFactorId;

    let headingForThisItem = null;
    for (let i = index - 1; i >= 0; i--) {
      if (costLineItems[i].isHeading) {
        headingForThisItem = costLineItems[i].heading || null;
        break;
      }
    }

    try {
      const response = await api.post('/line-items', {
        materialId: selectedMaterial,
        lineItems: [
          {
            id: currentFactorId,
            material_id: selectedMaterial,
            line_item_rows: updatedRows,
            factor: parseFloat(calculatedQuantity) || 0,
            measurement_unit_rate_id: updated[index].Code || null,
            headings: headingForThisItem,
          },
        ],
      });

      if (response.data?.success) {
        console.log('✅ Quantity calculator data saved successfully with heading:', headingForThisItem);
        setSuccess('Quantity calculator data saved successfully');
      } else {
        console.error('❌ Failed to save:', response.data);
        showErrorModal('Error', 'Failed to save quantity calculator data');
        return;
      }
    } catch (error) {
      console.error('Error saving line item rows:', error);
      showErrorModal(
        'Error',
        'Failed to save quantity calculator data: ' +
          (error.response?.data?.message || error.message)
      );
    }

    // ✅ Live links: propagate this item's new quantity to any rows linked to it,
    // recomputing dependent factors in the grid without needing a reload.
    const resolvedHistory = { ...rowHistory, [currentFactorId]: updatedRows };
    resolveLineItemLinks(updated, {
      rowsOf: (item) => resolvedHistory[item.FactorId],
      setRows: (item, rows) => { resolvedHistory[item.FactorId] = rows; },
      setQuantity: (item, qty) => {
        item.Factor = qty;
        item.TotalResult = qty * (parseFloat(item.Value) || 0);
      },
    });

    setRowHistory(resolvedHistory);
    setCostLineItems(updated);
    setModalOpen(false);
  };

  const openCalculatorModal = (index) => {
    setActiveLineItem({ index });
    setModalOpen(true);
  };

  const validateLineItems = () => {
    const dataLineItems = costLineItems.filter((item) => !item.isHeading);

    const incompleteItems = dataLineItems.filter((item) => {
      const hasCategory = item.Category && item.Category.trim() !== '';
      const hasDescription = item.CostMeasurementName && item.CostMeasurementName.trim() !== '';
      const hasCode = item.Code && item.Code.trim() !== '';

      return !hasCategory || !hasDescription || !hasCode;
    });

    return {
      isValid: incompleteItems.length === 0,
      incompleteItems: incompleteItems,
    };
  };

  const showComparison = () => {
    const validation = validateLineItems();

    if (!validation.isValid) {
      const itemDetails = validation.incompleteItems
        .map((item, idx) => {
          const issues = [];
          if (!item.Category || item.Category.trim() === '') issues.push('Category');
          if (!item.CostMeasurementName || item.CostMeasurementName.trim() === '')
            issues.push('Description');
          if (!item.Code || item.Code.trim() === '') issues.push('Code');

          return `  • Item ${idx + 1} (ID: ${item.FactorId}): Missing ${issues.join(', ')}`;
        })
        .join('\n');

      showErrorModal(
        'Cannot save material',
        `${validation.incompleteItems.length} line item(s) are incomplete.\n\n` +
          `Please select both Category and Description for all line items before saving:\n\n${itemDetails}\n\n` +
          `TIP: Click "Edit" on each incomplete line item and select a Category and Description from the dropdowns.`
      );
      return;
    }

    setShowComparisonModal(true);
  };

  const insertMaterial = async () => {
    try {
      setShowComparisonModal(false);
      setLoading(true);

      let currentHeading = null;
      const lineItemsToSave = [];
      let sortOrder = 0;

      // ✅ Live links: resolve linked rows against current source quantities so the
      // persisted factors reflect the latest source values.
      const resolvedHistory = { ...rowHistory };
      const resolvedItems = costLineItems.map((item) => ({ ...item }));
      resolveLineItemLinks(resolvedItems, {
        rowsOf: (item) => resolvedHistory[item.FactorId],
        setRows: (item, rows) => { resolvedHistory[item.FactorId] = rows; },
        setQuantity: (item, qty) => {
          item.Factor = qty;
          item.TotalResult = qty * (parseFloat(item.Value) || 0);
        },
      });

      // Build line items array
      resolvedItems.forEach((item) => {
        if (item.isHeading) {
          currentHeading = item.heading;
          return;
        }

        if (!item.Code || !item.Category || !item.CostMeasurementName) {
          console.warn('⚠️ Skipping incomplete item:', {
            FactorId: item.FactorId,
            hasCode: !!item.Code,
            hasCategory: !!item.Category,
            hasDescription: !!item.CostMeasurementName,
          });
          return;
        }

        lineItemsToSave.push({
          id: item.FactorId || uuidv4(),
          measurementUnitRateId: item.Code, // child_mur_id
          factor: parseFloat(item.Factor) || 0,
          childValue: parseFloat(item.Value) || 0,
          line_item_rows: resolvedHistory[item.FactorId] || [],
          heading: currentHeading,
          sort_order: sortOrder++,
        });
      });

      if (lineItemsToSave.length === 0) {
        showErrorModal(
          'Error',
          'No valid line items to save. Please ensure all line items have Category and Description selected.'
        );
        setLoading(false);
        return;
      }

      // Call the new API endpoint
      try {
        const response = await saveCostMaterialBreakdown(
          selectedMaterial,
          lineItemsToSave,
          selectedRegion
        );

        if (!response.success) {
          showErrorModal('Error', response.message || 'Failed to save cost material breakdown');
          return;
        }

        // ✅ Log the action
        logAction(
          auth.currentUser?.uid || 'unknown',
          auth.currentUser?.email || 'unknown',
          'INSERT_COST_MATERIAL',
          {
            materialId: selectedMaterial,
            regionId: selectedRegion,
            regionName,
            lineItemCount: lineItemsToSave.length,
            headingCount: [...new Set(lineItemsToSave.map((i) => i.heading).filter(Boolean))].length,
            totalCost: response.totalCost,
          }
        );

        // ✅ Show success modal with page reload on close
        setModal({
          open: true,
          title: 'Material Updated',
          message: `Cost material breakdown saved successfully for ${regionName}.\n\nTotal Cost: ${response.totalCost?.toFixed(2) || 'N/A'}`,
          icon: '🎉',
          onCloseCallback: () => {
            // ✅ Reload the entire page when user clicks OK
            window.location.reload();
          },
        });


      } catch (error) {
        console.error('❌ Error saving breakdown:', error);
        showErrorModal('Error', error.response?.data?.message || error.message);
      }
    } catch (error) {
      console.error('Error in insertMaterial:', error);
      showErrorModal('Error', 'An error occurred while saving the material.');
    } finally {
      setLoading(false);
    }
  };



  const deleteMaterial = async () => {
    if (!selectedMaterial) {
      showErrorModal('Error', 'No material selected for deletion.');
      return;
    }
    try {
      setLoading(true);
      
      // ✅ Use the new CostEditor API endpoint
      const response = await deleteCostMaterial(selectedMaterial, selectedRegion);

      if (response.success) {
        showModal(
          'Material Deleted',
          `Cost material and associated data deleted for ${regionName}.`,
          '🗑️'
        );
        setSuccess(`Cost material deleted successfully for ${regionName}!`);
        
        logAction(
          auth.currentUser?.uid || 'unknown',
          auth.currentUser?.email || 'unknown',
          'DELETE_COST_MATERIAL',
          {
            materialId: selectedMaterial,
            regionId: selectedRegion,
            regionName,
          }
        );
        
        // Clear selection and reload materials
        setSelectedMaterial('');
        setCostLineItems([]);
        await loadCostMaterials();
        
      } else {
        showErrorModal('Error', 'Failed to delete material.');
      }
    } catch (error) {
      console.error('Error deleting material:', error);
      showErrorModal('Error', error.response?.data?.message || 'An error occurred while deleting the material.');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };


  const renderCostSection = () => {
    const handleDragStart = (e, index) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', index.toString());
      e.currentTarget.style.opacity = '0.4';
    };

    const handleDragEnd = (e) => {
      e.currentTarget.style.opacity = '1';
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      return false;
    };

    const handleDrop = (e, dropIndex) => {
      e.stopPropagation();
      e.preventDefault();

      const dragIndex = parseInt(e.dataTransfer.getData('text/html'));

      if (dragIndex === dropIndex) return;

      const updated = [...costLineItems];
      const [draggedItem] = updated.splice(dragIndex, 1);
      updated.splice(dropIndex, 0, draggedItem);

      let currentHeading = null;
      updated.forEach((item, idx) => {
        if (item.isHeading) {
          currentHeading = item.heading;
        } else {
          updated[idx].heading = currentHeading;
        }
      });

      setCostLineItems([...updated]);
    };

    return (
      <section className="section section--line-items" data-section="Cost Line Items">
        <div className="section__head"></div>
        <h3 className="section__title">Cost Line Items</h3>

        <div className="line-items-header">
          <span>⋮⋮</span>
          <span>M_ID</span>
          <span>Category</span>
          <span>Description</span>
          <span>Code</span>
          <span>Unit</span>
          <span>Quantity</span>
          <span>Value</span>
          <span>Total</span>
          <span>ID</span>
          <span>Actions</span>
        </div>

        {costLineItems.map((item, index) => {
          const isIncomplete =
            !item.isHeading &&
            (!item.Category ||
              item.Category.trim() === '' ||
              !item.CostMeasurementName ||
              item.CostMeasurementName.trim() === '' ||
              !item.Code ||
              item.Code.trim() === '');

          return (
            <React.Fragment key={`${item.FactorId || 'new'}-${index}`}>
              {item.isHeading ? (
                <div
                  className="heading-item"
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <span className="drag-handle">⋮⋮</span>
                  <input
                    className="input input--heading"
                    type="text"
                    value={item.heading || ''}
                    placeholder="Enter heading..."
                    onChange={(e) => handleHeadingChange(index, e.target.value)}
                  />
                  <button className="btn btn--danger btn--small" onClick={() => deleteLineItem(index)}>
                    Delete
                  </button>
                </div>
              ) : (
                <div
                  className={`line-item ${isIncomplete ? 'line-item--incomplete' : ''}`}
                  data-row-index={index}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <span className="drag-handle" style={{ cursor: 'grab' }}>
                    ⋮⋮
                  </span>

                  {isIncomplete && (
                    <span
                      className="incomplete-indicator"
                      title="Please select Category and Description"
                    >
                      ⚠️
                    </span>
                  )}
                  <span
                    className="measurement-id-icon"
                    title={item.MeasurementId}
                    style={{
                      cursor: 'pointer',
                      fontSize: '16px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    ℹ️
                  </span>
                  {editingLineItems[index] ? (
                    <div style={{ minWidth: 200 }}>
                      <StyledDropdown
                        name={`cat-${item.FactorId || index}`}
                        options={uniqueCategoryOptions(costCategories)}
                        value={item.Category}
                        onChange={(val) => handleLineItemCategoryChange(index, val)}
                        placeholder="⚠️ Select Category"
                      />
                    </div>
                  ) : (
                    <input
                      className={`input input--readonly ${!item.Category ? 'input--missing' : ''}`}
                      type="text"
                      value={item.Category || '⚠️ REQUIRED'}
                      readOnly
                    />
                  )}

                  {editingLineItems[index] ? (
                    <div style={{ minWidth: 260 }}>
                      <StyledDropdown
                        name={`desc-${item.FactorId || index}`}
                        options={uniqueDescOptions(costCategories, item.Category)}
                        value={item.CostMeasurementName}
                        onChange={(val) => handleLineItemChange(index, 'description', val)}
                        placeholder="Select Description"
                      />
                    </div>
                  ) : (
                    <input
                      className={`input input--readonly ${!item.CostMeasurementName ? 'input--missing' : ''}`}
                      type="text"
                      value={item.CostMeasurementName || '⚠️ REQUIRED'}
                      readOnly
                    />
                  )}

                  <input className="input input--readonly" type="text" value={item.Code} readOnly />
                  <input
                    className="input input--readonly"
                    type="text"
                    value={item.QuantityTypeName}
                    readOnly
                  />

                  <input
                    className="input input--quantity"
                    type="text"
                    value={item.Factor}
                    onFocus={() => openCalculatorModal(index)}
                    readOnly
                  />

                  <input
                    className="input input--value"
                    type="number"
                    value={item.Value}
                    onChange={(e) => handleLineItemChange(index, 'Value', e.target.value)}
                  />

                  <input
                    className="input input--readonly"
                    type="number"
                    value={item.TotalResult}
                    readOnly
                  />
                  <input
                    className="input input--readonly"
                    type="text"
                    value={item.FactorId}
                    readOnly
                  />

                  <div className="btn-group btn-group--row-actions">
                    <button
                      className="btn btn--primary btn--edit-save"
                      onClick={() => toggleEdit(index)}
                      aria-label={editingLineItems[index] ? 'Save row' : 'Edit row'}
                    >
                      {editingLineItems[index] ? 'Save' : 'Edit'}
                    </button>
                    <button className="btn btn--danger btn--delete-row" onClick={() => deleteLineItem(index)}>
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}

        <div className="section__actions">
          <button className="btn btn--success btn--add-line" onClick={addLineItem}>
            + Add Line Item
          </button>
          <button className="btn btn--indigo btn--add-heading" onClick={addHeading}>
            + Add Heading
          </button>
        </div>
      </section>
    );
  };

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(t);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(''), 8000);
      return () => clearTimeout(t);
    }
  }, [error]);

  useEffect(() => {
    const anyOpen =
      showDeleteConfirm ||
      showComparisonModal ||
      modalOpen ||
      modal.open ||
      errorModal.open ||
      showEditNameModal ||
      showEditSKUModal ||        
      showEditVendorModal; 
    document.documentElement.classList.toggle('vq-modal-open', anyOpen);
    document.body.classList.toggle('vq-modal-open', anyOpen);
    return () => {
      document.documentElement.classList.remove('vq-modal-open');
      document.body.classList.remove('vq-modal-open');
    };
  }, [
    showDeleteConfirm,
    showComparisonModal,
    modalOpen,
    modal.open,
    errorModal.open,
    showEditNameModal,
    showEditSKUModal,          
    showEditVendorModal,
  ]);

  return (
    <div className="rate-editor-page">
      <h1 className="page-title">Cost Editor</h1>

      <div className="region-info">
        <span className="badge badge--region">Region: {regionName}</span>
        {selectedMaterial && <span className="badge badge--material">Material ID: {selectedMaterial}</span>}
      </div>

      {error && (
        <div className="alert alert--error" role="alert">
          <span className="alert__icon" aria-hidden>
            ⚠️
          </span>
          <span className="alert__text">{error}</span>
          <button className="alert__close" onClick={() => setError('')} aria-label="Dismiss error">
            ×
          </button>
        </div>
      )}
      {success && (
        <div className="alert alert--success" role="status">
          <span className="alert__icon" aria-hidden>
            ✅
          </span>
          <span className="alert__text">{success}</span>
          <button className="alert__close" onClick={() => setSuccess('')} aria-label="Dismiss message">
            ×
          </button>
        </div>
      )}
      {loading && (
        <div className="loading">
          <div className="spinner" aria-hidden></div>
          <span className="loading__text">Loading...</span>
        </div>
      )}

      <div className="form-container form-container--material">
        <label className="label" htmlFor="materialSearch">
          Search Cost Material
        </label>
        <input
          className="input input--search"
          type="text"
          id="materialSearch"
          placeholder="Search by ID or Name"
          value={searchQuery}
          onChange={handleSearchChange}
        />

        <label className="label" htmlFor="materialSelect">
          Select Cost Material
        </label>
        <div style={{ maxWidth: 480 }}>
          <StyledDropdown
            name="materialSelect"
            options={filteredMaterials.map((costItem) => ({
              value: costItem.MeasurementUnitRateId,  // ✅ Use MeasurementUnitRateId
              label: `${costItem.MeasurementUnitName} (ID: ${costItem.MeasurementUnitRateId})`,  // ✅ Use cost item name
            }))}
            value={selectedMaterial}
            onChange={(val) => handleMaterialChange({ target: { value: val } })}
            placeholder="Select Cost Material"
          />
        </div>

        <small className="hint">
          {filteredMaterials.length
            ? `${filteredMaterials.length} cost items available for ${regionName}`
            : 'No cost items found'}
        </small>
      </div>

      <div className="material-actions">
        <button
          onClick={handleEditNameClick}
          className="btn btn--edit-name edit-name-button"
          disabled={!selectedMaterial}
          title={!selectedMaterial ? 'Please select a material first' : 'Edit material name'}
        >
          ✏️ Edit Name
        </button>

        {/* 🆕 NEW: Edit SKU Button */}
        <button
          onClick={handleEditSKUClick}
          className="btn btn--edit-sku edit-sku-button"
          disabled={!selectedMaterial}
          title={!selectedMaterial ? 'Please select a material first' : 'Edit SKU'}
        >
          🏷️ Edit SKU
        </button>

        {/* 🆕 NEW: Edit Vendor Button */}
        <button
          onClick={handleEditVendorClick}
          className="btn btn--edit-vendor edit-vendor-button"
          disabled={!selectedMaterial}
          title={!selectedMaterial ? 'Please select a material first' : 'Edit Vendor'}
        >
          🏢 Edit Vendor
        </button>
      </div>

      {selectedMaterial && renderCostSection()}

      <QuantityCalculatorModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={(calculatedQuantity, updatedRows) => saveQuantityFromModal(calculatedQuantity, updatedRows)}
        initialRows={rowHistory[costLineItems[activeLineItem?.index]?.FactorId] || []}
        lineItems={costLineItems}
        sectionType="Cost"
        currentItemId={costLineItems[activeLineItem?.index]?.FactorId}
        lineItemRowsById={rowHistory}
      />

      <div className="control-buttons">
        <button onClick={showComparison} className="btn insert-button btn--primary-big">
          Update Cost Material Data
        </button>
        <button onClick={() => setShowDeleteConfirm(true)} className="btn delete-button btn--danger-big">
          Delete Cost Material
        </button>
      </div>

      {showEditNameModal && (
        <VQModal aria-label="Edit material name">
          <h2 className="modal__title">Edit Cost Material Name</h2>
          <div className="modal__content">
            <div className="form-group">
              <label className="label" htmlFor="newMaterialName">
                Material Name
              </label>
              <input
                className="input"
                type="text"
                id="newMaterialName"
                value={newMaterialName}
                onChange={(e) => setNewMaterialName(e.target.value)}
                placeholder="Enter new material name"
                maxLength={255}
              />
              <small className="hint">Current name: {originalMaterialName}</small>
            </div>
          </div>
          <div className="modal__actions">
            <button
              className="btn btn--success"
              onClick={handleSaveName}
              disabled={loading || !newMaterialName.trim()}
            >
              {loading ? 'Saving...' : 'Save Name'}
            </button>
            <button
              className="btn btn--secondary"
              onClick={() => {
                setShowEditNameModal(false);
                setNewMaterialName(originalMaterialName);
              }}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </VQModal>
      )}

      {/* 🆕 NEW: Edit SKU Modal */}
      {showEditSKUModal && (
        <VQModal aria-label="Edit SKU">
          <h2 className="modal__title">Edit SKU</h2>
          <div className="modal__content">
            <div className="form-group">
              <label className="label" htmlFor="materialNameDisplay">
                Material Name
              </label>
              <input
                className="input input--readonly"
                type="text"
                id="materialNameDisplay"
                value={materials.find((m) => String(m.MeasurementUnitRateId) === String(selectedMaterial))?.MeasurementUnitName || ''}
                readOnly
              />
            </div>
            <div className="form-group">
              <label className="label" htmlFor="originalSKU">
                Current SKU
              </label>
              <input
                className="input input--readonly"
                type="text"
                id="originalSKU"
                value={originalSKU || 'No SKU currently set'}
                readOnly
              />
            </div>
            <div className="form-group">
              <label className="label" htmlFor="newSKU">
                New SKU
              </label>
              <input
                className="input"
                type="text"
                id="newSKU"
                value={newSKU}
                onChange={(e) => setNewSKU(e.target.value)}
                placeholder="Enter SKU"
                maxLength={100}
              />
              <small className="hint">Enter product SKU or identifier</small>
            </div>
          </div>
          <div className="modal__actions">
            <button className="btn btn--success" onClick={handleSaveSKU} disabled={loading}>
              {loading ? 'Saving...' : 'Save SKU'}
            </button>
            <button
              className="btn btn--secondary"
              onClick={() => {
                setShowEditSKUModal(false);
                setNewSKU(originalSKU);
              }}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </VQModal>
      )}

      {/* 🆕 NEW: Edit Vendor Modal */}
      {showEditVendorModal && (
        <VQModal aria-label="Edit Vendor">
          <h2 className="modal__title">Edit Vendor</h2>
          <div className="modal__content">
            <div className="form-group">
              <label className="label" htmlFor="materialNameDisplay2">
                Material Name
              </label>
              <input
                className="input input--readonly"
                type="text"
                id="materialNameDisplay2"
                value={materials.find((m) => String(m.MeasurementUnitRateId) === String(selectedMaterial))?.MeasurementUnitName || ''}
                readOnly
              />
            </div>
            <div className="form-group">
              <label className="label" htmlFor="originalVendor">
                Current Vendor
              </label>
              <input
                className="input input--readonly"
                type="text"
                id="originalVendor"
                value={originalVendor || 'No vendor currently set'}
                readOnly
              />
            </div>
            <div className="form-group">
              <label className="label" htmlFor="newVendor">
                New Vendor
              </label>
              <input
                className="input"
                type="text"
                id="newVendor"
                value={newVendor}
                onChange={(e) => setNewVendor(e.target.value)}
                placeholder="Enter vendor name"
                maxLength={100}
              />
              <small className="hint">Enter supplier or vendor name</small>
            </div>
          </div>
          <div className="modal__actions">
            <button className="btn btn--success" onClick={handleSaveVendor} disabled={loading}>
              {loading ? 'Saving...' : 'Save Vendor'}
            </button>
            <button
              className="btn btn--secondary"
              onClick={() => {
                setShowEditVendorModal(false);
                setNewVendor(originalVendor);
              }}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </VQModal>
      )}


      {showDeleteConfirm && (
        <VQModal aria-label="Confirm deletion">
          <h2 className="modal__title">Confirm Deletion</h2>
          <p className="modal__text">
            Are you sure you want to delete this cost material from {regionName}? This action is
            irreversible.
          </p>
          <div className="modal__actions">
            <button className="btn btn--danger" onClick={deleteMaterial} disabled={loading}>
              {loading ? 'Deleting...' : 'Yes, Delete'}
            </button>
            <button
              className="btn btn--secondary"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </VQModal>
      )}

      {showComparisonModal && (
        <VQModal wide aria-label="Compare changes">
          <h2 className="modal__title">Compare Changes - {regionName}</h2>

          <div className="comparison-table">
            <div className="table-header">
              <span>Measurement ID (Before)</span>
              <span>Measurement ID (After)</span>
              <span>Description (Before)</span>
              <span>Description (After)</span>
              <span>Code (Before)</span>
              <span>Code (After)</span>
              <span>Unit (Before)</span>
              <span>Unit (After)</span>
              <span>Value (Before)</span>
              <span>Value (After)</span>
              <span>Factor (Before)</span>
              <span>Factor (After)</span>
              <span>Total (Before)</span>
              <span>Total (After)</span>
            </div>

            {getComparisonData().map(({ original, current }, index) => (
              <div
                className={`table-row ${index % 2 === 0 ? 'even-row' : 'odd-row'}`}
                key={`${original.FactorId}-${index}`}
              >
                <span>{original.MeasurementType}</span>
                <span className={original.MeasurementType !== current.MeasurementType ? 'diff' : ''}>
                  {current.MeasurementType || '-'}
                </span>
                <span>{original.CostMeasurementName}</span>
                <span className={original.CostMeasurementName !== current.CostMeasurementName ? 'diff' : ''}>
                  {current.CostMeasurementName || '-'}
                </span>
                <span>{original.MeasurementUnitRateId || '-'}</span>
                <span className={(original.MeasurementUnitRateId || '-') !== (current.Code || '-') ? 'diff' : ''}>
                  {current.Code || '-'}
                </span>
                <span>{original.QuantityTypeName}</span>
                <span className={original.QuantityTypeName !== current.QuantityTypeName ? 'diff' : ''}>
                  {current.QuantityTypeName || '-'}
                </span>
                <span>{original.Value}</span>
                <span className={parseFloat(original.Value) !== parseFloat(current.Value) ? 'diff' : ''}>
                  {current.Value || '-'}
                </span>
                <span>{original.Factor}</span>
                <span className={parseFloat(original.Factor) !== parseFloat(current.Factor) ? 'diff' : ''}>
                  {current.Factor || '-'}
                </span>
                <span>{original.TotalResult}</span>
                <span
                  className={parseFloat(original.TotalResult) !== parseFloat(current.TotalResult) ? 'diff' : ''}
                >
                  {current.TotalResult || '-'}
                </span>
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <button className="btn proceed-button btn--success" onClick={insertMaterial} disabled={loading}>
              {loading ? 'Updating...' : 'Proceed with Update'}
            </button>
            <button
              className="btn cancel-button btn--secondary"
              onClick={() => setShowComparisonModal(false)}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </VQModal>
      )}

      <ErrorModal
        isOpen={errorModal.open}
        onClose={() => setErrorModal((m) => ({ ...m, open: false }))}
        title={errorModal.title}
        message={errorModal.message}
        icon={errorModal.icon}
      />

      <SuccessModal
        isOpen={modal.open}
        onClose={() => {
          // ✅ Call the callback if it exists, otherwise just close
          if (modal.onCloseCallback) {
            modal.onCloseCallback();
          } else {
            setModal((m) => ({ ...m, open: false }));
          }
        }}
        title={modal.title}
        message={modal.message}
        icon={modal.icon}
      />
    </div>
  );
}

export default CostEditor;
